import { Modal, Stack, Text, Loader, ThemeIcon, Group, Box, Paper, Center, Button } from '@mantine/core';
import { MaterialSymbol } from 'react-material-symbols';
import { useEffect, useState, useCallback } from 'react';
import { OrderDTO } from '../../../interfaces/OrderInterfaces';
import { useAcceptOrderMutation } from '../../../services/OrderService';

interface AcceptStep {
    type: 'VALIDATE' | 'ACCEPT' | 'COMPLETED' | 'ERROR';
    status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'ERROR';
    message: string;
    details?: string;
}

interface MiraklAcceptOrderModalProps {
    opened: boolean;
    onClose: () => void;
    order: OrderDTO | null;
    onSuccess?: () => void;
}

const MiraklAcceptOrderModal = ({ opened, onClose, order, onSuccess }: MiraklAcceptOrderModalProps) => {
    const [steps, setSteps] = useState<AcceptStep[]>([]);
    const [isCompleted, setIsCompleted] = useState(false);
    const [isError, setIsError] = useState(false);
    const [isRunning, setIsRunning] = useState(false);
    const [acceptOrder] = useAcceptOrderMutation();

    const startAcceptance = useCallback(async () => {
        if (!order || isRunning) return;

        setIsRunning(true);
        setSteps([]);
        setIsCompleted(false);
        setIsError(false);

        // Step 1: Validating
        setSteps([{
            type: 'VALIDATE',
            status: 'IN_PROGRESS',
            message: 'Validazione ordine...'
        }]);

        // Simulate brief validation
        await new Promise(resolve => setTimeout(resolve, 500));

        setSteps(prev => prev.map(s => 
            s.type === 'VALIDATE' 
                ? { ...s, status: 'COMPLETED', message: 'Ordine validato' }
                : s
        ));

        // Step 2: Accepting
        setSteps(prev => [...prev, {
            type: 'ACCEPT',
            status: 'IN_PROGRESS',
            message: 'Invio accettazione a Mirakl...'
        }]);

        try {
            const result = await acceptOrder({
                marketplace: order.marketplace,
                orderId: order.orderId
            }).unwrap();

            if (result.success) {
                setSteps(prev => prev.map(s => 
                    s.type === 'ACCEPT' 
                        ? { ...s, status: 'COMPLETED', message: 'Ordine accettato su Mirakl' }
                        : s
                ));

                // Step 3: Completed
                setSteps(prev => [...prev, {
                    type: 'COMPLETED',
                    status: 'COMPLETED',
                    message: 'Processo completato!'
                }]);

                setIsCompleted(true);
                onSuccess?.();
            } else {
                setSteps(prev => prev.map(s => 
                    s.type === 'ACCEPT' 
                        ? { ...s, status: 'ERROR', message: 'Errore accettazione', details: result.message }
                        : s
                ));
                setIsError(true);
            }
        } catch (error: any) {
            setSteps(prev => prev.map(s => 
                s.type === 'ACCEPT' 
                    ? { ...s, status: 'ERROR', message: 'Errore accettazione', details: error?.data?.message || 'Errore sconosciuto' }
                    : s
            ));
            setIsError(true);
        } finally {
            setIsRunning(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order, acceptOrder]);

    useEffect(() => {
        if (opened && order) {
            startAcceptance();
        }
    }, [opened, order, startAcceptance]);

    const handleClose = () => {
        if (isCompleted || isError) {
            onClose();
        }
    };

    const getStepIcon = (step: AcceptStep) => {
        if (step.status === 'IN_PROGRESS') {
            return <Loader size="xs" color="blue" />;
        }
        if (step.status === 'COMPLETED') {
            return (
                <ThemeIcon size="sm" radius="xl" color="teal" variant="filled">
                    <MaterialSymbol icon="check" size={14} />
                </ThemeIcon>
            );
        }
        if (step.status === 'ERROR') {
            return (
                <ThemeIcon size="sm" radius="xl" color="red" variant="filled">
                    <MaterialSymbol icon="close" size={14} />
                </ThemeIcon>
            );
        }
        return (
            <ThemeIcon size="sm" radius="xl" color="gray" variant="light">
                <MaterialSymbol icon="schedule" size={14} />
            </ThemeIcon>
        );
    };

    if (!order) return null;

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title={null}
            centered
            size="sm"
            closeOnClickOutside={isCompleted || isError}
            closeOnEscape={isCompleted || isError}
            withCloseButton={isCompleted || isError}
            styles={{
                header: { display: isCompleted || isError ? 'flex' : 'none' },
                body: { padding: '2rem' }
            }}
        >
            <Stack align="center" gap="lg" p="md">
                {/* Icon */}
                <ThemeIcon 
                    size={70} 
                    radius="xl" 
                    color={isCompleted ? 'teal' : isError ? 'red' : 'blue'} 
                    variant="light"
                >
                    {isCompleted ? (
                        <MaterialSymbol icon="check_circle" size={45} fill />
                    ) : isError ? (
                        <MaterialSymbol icon="error" size={45} fill />
                    ) : (
                        <Loader size="md" color="blue" />
                    )}
                </ThemeIcon>

                {/* Title */}
                <Text size="lg" fw={600} ta="center">
                    {isCompleted ? 'Ordine Accettato!' : isError ? 'Errore' : 'Accettazione Ordine'}
                </Text>

                {/* Warning message */}
                {!isCompleted && !isError && (
                    <Text size="sm" c="dimmed" ta="center" fs="italic">
                        La conferma dell'ordine potrebbe richiedere fino a un minuto.
                        Attendere il completamento dell'operazione.
                    </Text>
                )}

                {/* Steps list */}
                <Paper withBorder p="md" radius="md" w="100%">
                    <Stack gap="xs">
                        {steps.map((step, index) => (
                            <Group key={index} gap="sm" wrap="nowrap" align="flex-start">
                                {getStepIcon(step)}
                                <Box style={{ flex: 1 }}>
                                    <Text 
                                        size="sm" 
                                        fw={step.status === 'IN_PROGRESS' ? 500 : 400}
                                        c={step.status === 'IN_PROGRESS' ? 'blue' : step.status === 'COMPLETED' ? 'teal' : step.status === 'ERROR' ? 'red' : 'dimmed'}
                                    >
                                        {step.message}
                                    </Text>
                                    {step.details && step.status === 'COMPLETED' && (
                                        <Text size="xs" c="dimmed">{step.details}</Text>
                                    )}
                                    {step.details && step.status === 'ERROR' && (
                                        <Text size="xs" c="red.7" mt={2}>
                                            {step.details}
                                        </Text>
                                    )}
                                </Box>
                            </Group>
                        ))}
                        
                        {steps.length === 0 && !isError && (
                            <Center>
                                <Text size="sm" c="dimmed">Avvio in corso...</Text>
                            </Center>
                        )}
                    </Stack>
                </Paper>

                {/* Order info */}
                {isCompleted && (
                    <Paper withBorder p="md" radius="md" w="100%" bg="teal.0">
                        <Group justify="center" gap="xs">
                            <MaterialSymbol icon="shopping_bag" size={20} color="teal" />
                            <Text size="md" fw={600} c="teal.8">
                                {order.orderId}
                            </Text>
                        </Group>
                    </Paper>
                )}

                {/* Error actions */}
                {isError && (
                    <Group justify="center" gap="sm">
                        <Button 
                            variant="light" 
                            color="blue"
                            leftSection={<MaterialSymbol icon="refresh" size={18} />}
                            onClick={() => startAcceptance()}
                        >
                            Riprova
                        </Button>
                        <Button 
                            variant="subtle" 
                            color="gray"
                            onClick={handleClose}
                        >
                            Chiudi
                        </Button>
                    </Group>
                )}
            </Stack>
        </Modal>
    );
};

export default MiraklAcceptOrderModal;
